import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface ConfirmationOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  cancelButtonClass?: string;
  icon?: string;
  type?: 'warning' | 'danger' | 'info' | 'question';
  hideCancelButton?: boolean;
}

export interface ConfirmationResult {
  confirmed: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmationService {
  private confirmationSubject = new Subject<{ options: ConfirmationOptions; result: Subject<ConfirmationResult> }>();
  public confirmation$: Observable<{ options: ConfirmationOptions; result: Subject<ConfirmationResult> }> = 
    this.confirmationSubject.asObservable();

  constructor() { }

  /**
   * Exibe um diálogo de confirmação genérico
   * @param options Opções de confirmação
   * @returns Observable que emite true se confirmado, false se cancelado
   */
  confirm(options: ConfirmationOptions): Observable<boolean> {
    const resultSubject = new Subject<ConfirmationResult>();
    
    // Configurações padrão
    const defaultOptions: ConfirmationOptions = {
      title: 'Confirmação',
      message: options.message,
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'btn-secondary',
      icon: 'fas fa-question-circle',
      type: 'question'
    };

    const finalOptions = { ...defaultOptions, ...options };

    // Emite a confirmação para o componente
    this.confirmationSubject.next({
      options: finalOptions,
      result: resultSubject
    });

    // Retorna um Observable que emite true/false
    return new Observable<boolean>(observer => {
      resultSubject.subscribe({
        next: (result: ConfirmationResult) => {
          observer.next(result.confirmed);
          observer.complete();
        }
      });
    });
  }

  /**
   * Método helper para confirmação de exclusão
   * @param itemName Nome do item a ser excluído
   * @param itemType Tipo do item (ex: 'condutor', 'contrato', etc.)
   * @returns Observable que emite true se confirmado
   */
  confirmDelete(itemName: string, itemType: string = 'item'): Observable<boolean> {
    return this.confirm({
      title: 'Confirmar Exclusão',
      message: `Tem certeza que deseja excluir ${itemType} "${itemName}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-secondary',
      icon: 'fas fa-exclamation-triangle',
      type: 'danger'
    });
  }

  /**
   * Método helper para confirmação genérica com mensagem customizada
   * @param message Mensagem de confirmação
   * @param title Título do diálogo
   * @returns Observable que emite true se confirmado
   */
  confirmAction(message: string, title: string = 'Confirmação'): Observable<boolean> {
    return this.confirm({
      title,
      message,
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'btn-secondary',
      icon: 'fas fa-question-circle',
      type: 'question'
    });
  }

  /**
   * Confirma cancelamento de checklist (POST /checklist/{id}/cancel).
   */
  confirmCancelChecklist(checklistId: number, plate?: string): Observable<boolean> {
    const plateLabel = plate && plate !== 'N/A' ? ` (${plate})` : '';
    return this.confirm({
      title: 'Cancelar checklist',
      message: `Deseja cancelar o checklist #${checklistId}${plateLabel}? O checklist será marcado como cancelado.`,
      confirmText: 'Sim, cancelar',
      cancelText: 'Não',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-secondary',
      icon: 'fas fa-ban',
      type: 'danger'
    });
  }
}
